// TaskBatchCreator.jsx - With Excel Upload, Sheet Selection & Empty Row Filtering
import React, { useState, useEffect, useRef } from "react";
import {
  Modal,
  Button,
  Form,
  Row,
  Col,
  Table,
  Badge,
  Alert,
  Spinner,
} from "react-bootstrap";
import { toast } from "react-toastify";
import moment from "moment";
import * as XLSX from "xlsx";
import { saveTaskDetails, getContactGuid } from "../../Api/TaskMaster";
import { getAllTaskStatus, getAllTaskTypes } from "../../Api/TaskMaster";
import { ticketDropdownApi } from "../../Api/TicketApiOrg";
import { fetchStagesByProject } from "../../Api/StageMaster";
import "./TaskBatchCreator.css";

const TaskBatchCreator = ({
  show,
  onHide,
  projectId,
  orgId,
  userId,
  teamMembers,
  onSuccess,
  existingTasks = [],
}) => {
  const [tasks, setTasks] = useState([]);
  const [selectedTaskIndices, setSelectedTaskIndices] = useState(new Set());
  const [bulkEditMode, setBulkEditMode] = useState(false);
  const [bulkValues, setBulkValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [showValidation, setShowValidation] = useState(false);
  const [duplicateDetection, setDuplicateDetection] = useState(true);
  const [showExcelModal, setShowExcelModal] = useState(false);
  const [excelData, setExcelData] = useState([]);
  const [excelHeaders, setExcelHeaders] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  // Sheet selection states
  const [availableSheets, setAvailableSheets] = useState([]);
  const [selectedSheet, setSelectedSheet] = useState("");
  const [workbookData, setWorkbookData] = useState(null);

  // Dynamic data states
  const [assignedToOptions, setAssignedToOptions] = useState([]);
  const [loadingAssignedTo, setLoadingAssignedTo] = useState(false);
  const [taskTypes, setTaskTypes] = useState([]);
  const [loadingTaskTypes, setLoadingTaskTypes] = useState(false);
  const [statusList, setStatusList] = useState([]);
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [stages, setStages] = useState([]);
  const [loadingStages, setLoadingStages] = useState(false);

  const [selectedColumns, setSelectedColumns] = useState({
    taskName: null,
    taskDescription: null,
    assignedTo: null,
    taskStartDate: null,
    taskEndDate: null,
    taskPriority: null,
    taskStatus: null,
    taskType: null,
    stageBelong: null,
  });

  const hasInitialized = useRef(false);
  const fileInputRef = useRef(null);
  const username =
    JSON.parse(localStorage.getItem("userDetails"))?.username || "";

  const priorities = [
    { value: "Low", label: "Low" },
    { value: "Medium", label: "Medium" },
    { value: "High", label: "High" },
    { value: "Critical", label: "Critical" },
  ];

  // Load assignedTo options
  const loadAssignedToOptions = async () => {
    if (!orgId || !projectId) {
      setAssignedToOptions([]);
      return;
    }

    setLoadingAssignedTo(true);
    try {
      const [team, customers, partners] = await Promise.all([
        ticketDropdownApi.getTeamAssigned(orgId, projectId),
        ticketDropdownApi.getCustomer(orgId, projectId),
        ticketDropdownApi.getPartner(orgId, projectId),
      ]);

      const assigneeMap = new Map();

      team.forEach((item) => {
        if (!assigneeMap.has(item.contactGUID)) {
          assigneeMap.set(item.contactGUID, {
            value: item.contactGUID,
            label: `${item.contactName} (Team)`,
            type: "Team",
            contactName: item.contactName,
          });
        }
      });

      customers.forEach((item) => {
        if (!assigneeMap.has(item.contactGUID)) {
          assigneeMap.set(item.contactGUID, {
            value: item.contactGUID,
            label: `${item.contactName} (Customer)`,
            type: "Customer",
            contactName: item.contactName,
          });
        }
      });

      partners.forEach((item) => {
        if (!assigneeMap.has(item.contactGUID)) {
          assigneeMap.set(item.contactGUID, {
            value: item.contactGUID,
            label: `${item.contactName} (Partner)`,
            type: "Partner",
            contactName: item.contactName,
          });
        }
      });

      const sortedOptions = Array.from(assigneeMap.values()).sort((a, b) =>
        a.label.localeCompare(b.label),
      );

      setAssignedToOptions(sortedOptions);
    } catch (error) {
      console.error("Error loading assigned to options:", error);
      setAssignedToOptions([]);
    } finally {
      setLoadingAssignedTo(false);
    }
  };

  // Load task types
  const loadTaskTypes = async () => {
    try {
      setLoadingTaskTypes(true);
      const data = await getAllTaskTypes();
      const sorted = data.sort((a, b) => a.typeName.localeCompare(b.typeName));
      setTaskTypes(sorted);
    } catch (error) {
      console.error("Error loading task types:", error);
      setTaskTypes([]);
    } finally {
      setLoadingTaskTypes(false);
    }
  };

  // Load task statuses
  const loadTaskStatus = async () => {
    try {
      setLoadingStatus(true);
      const data = await getAllTaskStatus();
      const sorted = data.sort((a, b) =>
        a.statusName.localeCompare(b.statusName),
      );
      setStatusList(sorted);
    } catch (error) {
      console.error("Error loading status:", error);
      setStatusList([]);
    } finally {
      setLoadingStatus(false);
    }
  };

  // Load stages
  const loadStages = async () => {
    if (!projectId) {
      setStages([]);
      return;
    }

    setLoadingStages(true);
    try {
      const stagesData = await fetchStagesByProject(projectId);
      console.log("Loaded stages:", stagesData);
      setStages(stagesData || []);
    } catch (error) {
      console.error("Error loading stages:", error);
      setStages([]);
    } finally {
      setLoadingStages(false);
    }
  };

  useEffect(() => {
    if (show && projectId) {
      loadAssignedToOptions();
      loadTaskTypes();
      loadTaskStatus();
      loadStages();
    }
  }, [show, projectId, orgId]);

  const handleClose = () => {
    setTasks([]);
    setSelectedTaskIndices(new Set());
    setBulkEditMode(false);
    setBulkValues({});
    setShowValidation(false);
    setShowExcelModal(false);
    setExcelData([]);
    setExcelHeaders([]);
    setSelectedFile(null);
    setAvailableSheets([]);
    setSelectedSheet("");
    setWorkbookData(null);
    setSelectedColumns({
      taskName: null,
      taskDescription: null,
      assignedTo: null,
      taskStartDate: null,
      taskEndDate: null,
      taskPriority: null,
      taskStatus: null,
      taskType: null,
      stageBelong: null,
    });
    hasInitialized.current = false;
    onHide();
  };

  useEffect(() => {
    if (show && !hasInitialized.current) {
      hasInitialized.current = true;
      if (existingTasks.length > 0) {
        const clonedTasks = existingTasks.map((task) => ({
          id: `temp_${Date.now()}_${Math.random()}`,
          taskName: `${task.taskName} (Copy)`,
          taskDescription: task.taskDescription || "",
          assignedTo: task.assignedTo || "",
          taskStartDate: task.taskStartDate || "",
          taskEndDate: task.taskEndDate || "",
          taskPriority: task.taskPriority || "Medium",
          taskStatus: task.taskStatus || "",
          taskType: task.taskType || "",
          stageBelong: task.stageBelong || "",
          errors: {},
        }));
        setTasks(clonedTasks);
      } else {
        addNewTasks(3);
      }
    }
    if (!show) {
      hasInitialized.current = false;
    }
  }, [show, existingTasks]);

  // Function to check if a row has any meaningful data
  const isRowEmpty = (row) => {
    if (!row || !Array.isArray(row)) return true;

    // Check each cell in the row
    for (let i = 0; i < row.length; i++) {
      const cell = row[i];
      if (cell !== null && cell !== undefined) {
        if (typeof cell === "string" && cell.trim() !== "") {
          return false;
        }
        if (typeof cell === "number" && !isNaN(cell)) {
          return false;
        }
        if (cell instanceof Date && !isNaN(cell.getTime())) {
          return false;
        }
        if (cell === true || cell === false) {
          return false;
        }
      }
    }
    return true;
  };

  // Function to filter out completely empty rows and rows without meaningful task data
  const filterValidRows = (rows, headers) => {
    // Find potential task name or description column indices
    const taskNameIndex = headers.findIndex(
      (h) =>
        h &&
        h
          .toString()
          .toLowerCase()
          .match(/task|name|title|activity|work/),
    );
    const taskDescIndex = headers.findIndex(
      (h) =>
        h &&
        h
          .toString()
          .toLowerCase()
          .match(/description|desc|details|notes/),
    );

    return rows.filter((row) => {
      // Skip if row is completely empty
      if (isRowEmpty(row)) return false;

      // If we found a task name column, check if it has a value
      if (taskNameIndex !== -1) {
        const taskName = row[taskNameIndex];
        if (taskName && taskName.toString().trim() !== "") {
          return true;
        }
      }

      // If we found a description column, check if it has a value
      if (taskDescIndex !== -1) {
        const taskDesc = row[taskDescIndex];
        if (taskDesc && taskDesc.toString().trim() !== "") {
          return true;
        }
      }

      // If no specific columns found, check if any cell has meaningful data
      const hasMeaningfulData = row.some((cell) => {
        if (!cell) return false;
        const cellStr = cell.toString().trim();
        return cellStr !== "" && cellStr.length > 0;
      });

      return hasMeaningfulData;
    });
  };

  // Read Excel file and extract sheets
  const readExcelFile = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: "array" });
          const sheetNames = workbook.SheetNames;

          resolve({ workbook, sheetNames });
        } catch (error) {
          reject(error);
        }
      };

      reader.onerror = (error) => reject(error);
      reader.readAsArrayBuffer(file);
    });
  };

  // Load specific sheet data
  const loadSheetData = (workbook, sheetName) => {
    try {
      const sheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json(sheet, {
        header: 1,
        defval: "",
      });

      if (!jsonData || jsonData.length === 0) {
        return { headers: [], rows: [] };
      }

      const headers = jsonData[0] || [];
      let rows = jsonData.slice(1);

      // Filter out empty rows
      rows = filterValidRows(rows, headers);

      return { headers, rows };
    } catch (error) {
      console.error("Error loading sheet data:", error);
      return { headers: [], rows: [] };
    }
  };

  // Handle Excel file upload
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const validTypes = [
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/vnd.ms-excel",
      "text/csv",
    ];

    if (
      !validTypes.includes(file.type) &&
      !file.name.match(/\.(xlsx|xls|csv)$/)
    ) {
      toast.error("Please upload a valid Excel file (.xlsx, .xls, or .csv)");
      return;
    }

    setUploading(true);
    setSelectedFile(file);

    try {
      const { workbook, sheetNames } = await readExcelFile(file);

      setWorkbookData(workbook);
      setAvailableSheets(sheetNames);
      setSelectedSheet(sheetNames[0]);

      // Load first sheet data
      const { headers, rows } = loadSheetData(workbook, sheetNames[0]);

      if (rows.length > 0) {
        setExcelHeaders(headers);
        setExcelData(rows);

        // Auto-detect column mappings
        const autoMapped = detectColumnMappings(headers);
        setSelectedColumns(autoMapped);

        toast.success(
          `Loaded ${rows.length} tasks from sheet "${sheetNames[0]}"`,
        );
      } else {
        toast.warning(`No valid data found in sheet "${sheetNames[0]}"`);
        setExcelHeaders([]);
        setExcelData([]);
      }
    } catch (error) {
      console.error("Error reading file:", error);
      toast.error("Failed to read the Excel file");
    } finally {
      setUploading(false);
    }
  };

  // Handle sheet change
  const handleSheetChange = async (sheetName) => {
    if (!workbookData || !sheetName) return;

    setUploading(true);
    setSelectedSheet(sheetName);

    try {
      const { headers, rows } = loadSheetData(workbookData, sheetName);

      if (rows.length > 0) {
        setExcelHeaders(headers);
        setExcelData(rows);

        // Auto-detect column mappings for new sheet
        const autoMapped = detectColumnMappings(headers);
        setSelectedColumns(autoMapped);

        toast.success(`Loaded ${rows.length} tasks from sheet "${sheetName}"`);
      } else {
        toast.warning(`No valid data found in sheet "${sheetName}"`);
        setExcelHeaders([]);
        setExcelData([]);
      }
    } catch (error) {
      console.error("Error loading sheet:", error);
      toast.error("Failed to load the selected sheet");
    } finally {
      setUploading(false);
    }
  };

  // Auto-detect column mappings
  const detectColumnMappings = (headers) => {
    const mappings = {};
    const headerLower = headers.map((h) => (h || "").toString().toLowerCase());

    const fieldKeywords = {
      taskName: [
        "task",
        "name",
        "task name",
        "title",
        "activity",
        "work",
        "description",
      ],
      taskDescription: [
        "description",
        "desc",
        "details",
        "notes",
        "comments",
        "specifications",
        "task",
        "work",
      ],
      assignedTo: [
        "assignee",
        "assigned to",
        "assigned",
        "owner",
        "responsible",
        "resource",
        "contact",
        "assigned by",
      ],
      taskStartDate: [
        "start",
        "start date",
        "from",
        "begin",
        "start day",
        "date",
      ],
      taskEndDate: [
        "end",
        "end date",
        "to",
        "due",
        "due date",
        "deadline",
        "finish",
      ],
      taskPriority: ["priority", "level", "importance", "severity"],
      taskStatus: ["status", "state", "progress", "stage"],
      taskType: ["type", "task type", "category", "classification"],
      stageBelong: ["stage", "phase", "sprint", "milestone"],
    };

    Object.keys(fieldKeywords).forEach((field) => {
      const keywords = fieldKeywords[field];
      let bestMatchIndex = -1;
      let bestMatchScore = 0;

      headerLower.forEach((header, idx) => {
        keywords.forEach((keyword) => {
          if (header.includes(keyword)) {
            const score = keyword.length;
            if (score > bestMatchScore) {
              bestMatchScore = score;
              bestMatchIndex = idx;
            }
          }
        });
      });

      mappings[field] = bestMatchIndex !== -1 ? bestMatchIndex : null;
    });

    return mappings;
  };

  // Format Excel date
  const formatExcelDate = (dateValue) => {
    if (!dateValue) return "";

    // If it's a number (Excel serial date)
    if (typeof dateValue === "number") {
      try {
        const date = XLSX.SSF.parse_date_code(dateValue);
        if (date) {
          return `${date.y}-${String(date.m).padStart(2, "0")}-${String(date.d).padStart(2, "0")}`;
        }
      } catch (e) {
        console.error("Excel date parsing error:", e);
      }
    }

    // If it's a string date
    if (typeof dateValue === "string") {
      // Try to parse common date formats
      const datePatterns = [
        /(\d{4})-(\d{2})-(\d{2})/, // YYYY-MM-DD
        /(\d{2})\/(\d{2})\/(\d{4})/, // MM/DD/YYYY
        /(\d{2})-(\d{2})-(\d{4})/, // MM-DD-YYYY
        /(\w+)\s(\d{1,2}),\s(\d{4})/, // Month DD, YYYY
      ];

      for (const pattern of datePatterns) {
        const match = dateValue.match(pattern);
        if (match) {
          const parsed = new Date(dateValue);
          if (!isNaN(parsed.getTime())) {
            return parsed.toISOString().split("T")[0];
          }
        }
      }
    }

    // Try native Date parsing
    try {
      const parsed = new Date(dateValue);
      if (!isNaN(parsed.getTime())) {
        return parsed.toISOString().split("T")[0];
      }
    } catch (e) {
      console.error("Date parsing error:", e);
    }

    return dateValue;
  };

  // Add tasks from Excel
  const addTasksFromExcel = () => {
    if (excelData.length === 0) {
      toast.error("No data to import");
      return;
    }

    if (
      selectedColumns.taskName === null ||
      selectedColumns.taskName === undefined
    ) {
      toast.error("Please select a column for Task Name (required field)");
      return;
    }

    const newTasks = [];
    const skippedRows = [];

    excelData.forEach((row, idx) => {
      const taskNameRaw =
        selectedColumns.taskName !== null ? row[selectedColumns.taskName] : "";
      const taskName = taskNameRaw ? taskNameRaw.toString().trim() : "";

      if (!taskName) {
        skippedRows.push(idx + 2); // +2 because of 0-index and header row
        return;
      }

      let taskDescription =
        selectedColumns.taskDescription !== null
          ? row[selectedColumns.taskDescription] || ""
          : "";
      let assignedTo =
        selectedColumns.assignedTo !== null
          ? row[selectedColumns.assignedTo] || ""
          : "";
      let taskStartDate =
        selectedColumns.taskStartDate !== null
          ? row[selectedColumns.taskStartDate] || ""
          : "";
      let taskEndDate =
        selectedColumns.taskEndDate !== null
          ? row[selectedColumns.taskEndDate] || ""
          : "";
      let taskPriority =
        selectedColumns.taskPriority !== null
          ? row[selectedColumns.taskPriority] || "Medium"
          : "Medium";
      let taskStatus =
        selectedColumns.taskStatus !== null
          ? row[selectedColumns.taskStatus] || ""
          : "";
      let taskType =
        selectedColumns.taskType !== null
          ? row[selectedColumns.taskType] || ""
          : "";
      let stageBelong =
        selectedColumns.stageBelong !== null
          ? row[selectedColumns.stageBelong] || ""
          : "";

      // Convert to string if needed
      if (taskDescription && typeof taskDescription !== "string") {
        taskDescription = String(taskDescription);
      }
      if (assignedTo && typeof assignedTo !== "string") {
        assignedTo = String(assignedTo);
      }

      // Format dates
      if (taskStartDate) {
        taskStartDate = formatExcelDate(taskStartDate);
      }
      if (taskEndDate) {
        taskEndDate = formatExcelDate(taskEndDate);
      }

      // Map assignee by name
      if (assignedTo && assignedToOptions.length > 0) {
        const matchedOption = assignedToOptions.find(
          (opt) =>
            opt.contactName?.toLowerCase() === assignedTo.toLowerCase() ||
            opt.label?.toLowerCase().includes(assignedTo.toLowerCase()),
        );
        if (matchedOption) {
          assignedTo = matchedOption.value;
        }
      }

      // Map task type by name
      if (taskType && taskTypes.length > 0) {
        const matchedType = taskTypes.find(
          (t) => t.typeName?.toLowerCase() === taskType.toLowerCase(),
        );
        if (matchedType) {
          taskType = matchedType.typeGuid;
        }
      }

      // Map status by name
      if (taskStatus && statusList.length > 0) {
        const matchedStatus = statusList.find(
          (s) => s.statusName?.toLowerCase() === taskStatus.toLowerCase(),
        );
        if (matchedStatus) {
          taskStatus = matchedStatus.statusGuid;
        }
      }

      // Map stage by name
      if (stageBelong && stages.length > 0) {
        const matchedStage = stages.find(
          (s) => s.stageName?.toLowerCase() === stageBelong.toLowerCase(),
        );
        if (matchedStage) {
          stageBelong = matchedStage.stageId;
        }
      }

      // Validate priority
      const validPriorities = priorities.map((p) => p.value);
      if (!validPriorities.includes(taskPriority)) {
        taskPriority = "Medium";
      }

      newTasks.push({
        id: `temp_${Date.now()}_${Math.random()}_${idx}`,
        taskName: taskName,
        taskDescription: taskDescription,
        assignedTo: assignedTo,
        taskStartDate: taskStartDate,
        taskEndDate: taskEndDate,
        taskPriority: taskPriority,
        taskStatus: taskStatus,
        taskType: taskType,
        stageBelong: stageBelong,
        errors: {},
      });
    });

    if (skippedRows.length > 0) {
      toast.warning(
        `Skipped ${skippedRows.length} rows (rows ${skippedRows.join(", ")}) due to missing task name`,
      );
    }

    if (newTasks.length === 0) {
      toast.error("No valid tasks found. Make sure Task Name column has data.");
      return;
    }

    setTasks((prev) => [...prev, ...newTasks]);
    setShowExcelModal(false);
    setExcelData([]);
    setExcelHeaders([]);
    setSelectedFile(null);
    setAvailableSheets([]);
    setSelectedSheet("");
    setWorkbookData(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    toast.success(`Added ${newTasks.length} tasks from Excel file`);
  };

  const addNewTasks = (count = 1) => {
    const newTasks = Array(count)
      .fill()
      .map(() => ({
        id: `temp_${Date.now()}_${Math.random()}`,
        taskName: "",
        taskDescription: "",
        assignedTo: "",
        taskStartDate: "",
        taskEndDate: "",
        taskPriority: "Medium",
        taskStatus: "",
        taskType: "",
        stageBelong: "",
        errors: {},
      }));
    setTasks((prev) => [...prev, ...newTasks]);
  };

  const removeTask = (index) => {
    setTasks((prev) => prev.filter((_, i) => i !== index));
    setSelectedTaskIndices((prev) => {
      const newSet = new Set();
      prev.forEach((i) => {
        if (i < index) newSet.add(i);
        else if (i > index) newSet.add(i - 1);
      });
      return newSet;
    });
  };

  const updateTask = (index, field, value) => {
    setTasks((prev) => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        [field]: value,
        errors: {
          ...updated[index].errors,
          [field]: validateField(field, value, updated[index]),
        },
      };
      return updated;
    });
  };

  const validateField = (field, value, task) => {
    switch (field) {
      case "taskName":
        if (!value || !value.trim()) return "Task name is required";
        if (duplicateDetection && checkDuplicateName(value, task.id)) {
          return "Duplicate task name detected";
        }
        return null;
      case "taskStartDate":
        if (
          value &&
          task.taskEndDate &&
          new Date(value) > new Date(task.taskEndDate)
        ) {
          return "Start date cannot be after end date";
        }
        return null;
      case "taskEndDate":
        if (
          value &&
          task.taskStartDate &&
          new Date(value) < new Date(task.taskStartDate)
        ) {
          return "End date cannot be before start date";
        }
        return null;
      default:
        return null;
    }
  };

  const checkDuplicateName = (name, currentId) => {
    return tasks.some(
      (t) =>
        t.id !== currentId && t.taskName?.toLowerCase() === name.toLowerCase(),
    );
  };

  const applyBulkUpdate = () => {
    if (selectedTaskIndices.size === 0) return;

    setTasks((prev) => {
      const updated = [...prev];
      selectedTaskIndices.forEach((index) => {
        updated[index] = {
          ...updated[index],
          ...bulkValues,
          errors: {
            ...updated[index].errors,
            ...Object.keys(bulkValues).reduce((acc, field) => {
              acc[field] = validateField(
                field,
                bulkValues[field],
                updated[index],
              );
              return acc;
            }, {}),
          },
        };
      });
      return updated;
    });

    setBulkEditMode(false);
    setBulkValues({});
    toast.success(`Updated ${selectedTaskIndices.size} tasks`);
  };

  const toggleTaskSelection = (index) => {
    setSelectedTaskIndices((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(index)) newSet.delete(index);
      else newSet.add(index);
      return newSet;
    });
  };

  const selectAllTasks = () => {
    if (selectedTaskIndices.size === tasks.length) {
      setSelectedTaskIndices(new Set());
    } else {
      setSelectedTaskIndices(new Set(tasks.map((_, i) => i)));
    }
  };

  const validateAllTasks = () => {
    let isValid = true;
    const updatedTasks = [...tasks];

    updatedTasks.forEach((task, index) => {
      const errors = {};
      if (!task.taskName?.trim()) {
        errors.taskName = "Task name is required";
        isValid = false;
      }
      if (task.taskStartDate && task.taskEndDate) {
        if (new Date(task.taskStartDate) > new Date(task.taskEndDate)) {
          errors.taskEndDate = "End date must be after start date";
          isValid = false;
        }
      }
      updatedTasks[index].errors = errors;
    });

    setTasks(updatedTasks);
    setShowValidation(true);

    if (!isValid) {
      toast.error("Please fix validation errors before saving");
    }
    return isValid;
  };

  const handleSave = async () => {
    if (!validateAllTasks()) return;

    setSaving(true);
    const validTasks = tasks.filter((task) => task.taskName?.trim());

    try {
      const contactResponse = await getContactGuid(orgId, userId);

      if (!contactResponse || !contactResponse.contactGuid) {
        toast.error("Unable to fetch contact GUID");
        setSaving(false);
        return;
      }

      const contactGuid = contactResponse.contactGuid;
      console.log("Fetched Contact GUID:", contactGuid);

      const createdTasks = [];
      const errors = [];

      for (const task of validTasks) {
        try {
          const taskData = {
            orgId: String(orgId),
            taskName: task.taskName,
            taskDescription: task.taskDescription || "",
            assignedTo: task.assignedTo || null,
            taskStartDate: task.taskStartDate || null,
            taskEndDate: task.taskEndDate || null,
            taskPriority: task.taskPriority || "Medium",
            taskStatus: task.taskStatus || null,
            taskType: task.taskType || null,
            stageBelong: task.stageBelong || null,
            projectBelong: projectId,
            createdBy: username || String(userId),
            editedBy: username || String(userId),
            TaskCreatedGuid: contactGuid,
          };

          Object.keys(taskData).forEach((key) => {
            if (taskData[key] === null || taskData[key] === undefined) {
              delete taskData[key];
            }
          });

          console.log("Creating task with data:", taskData);
          const response = await saveTaskDetails(taskData, username || userId);
          createdTasks.push(response);
        } catch (error) {
          console.error("Error creating task:", error);
          errors.push({
            task: task.taskName,
            error: error.response?.data?.title || error.message,
          });
        }
      }

      if (errors.length > 0) {
        toast.warning(
          `Created ${createdTasks.length} tasks, ${errors.length} failed`,
        );
        console.error("Failed tasks:", errors);
      } else {
        toast.success(`Successfully created ${createdTasks.length} tasks`);
      }

      onSuccess?.(createdTasks);
      handleClose();
    } catch (error) {
      console.error("Error saving tasks:", error);
      toast.error(error.response?.data?.title || "Failed to save tasks");
    } finally {
      setSaving(false);
    }
  };

  const renderColumnSelect = (field, label, required = false) => {
    return (
      <Form.Group className="tbc-column-select-group">
        <Form.Label className="tbc-column-select-label">
          {label} {required && <span className="tbc-required-star">*</span>}
        </Form.Label>
        <Form.Select
          size="sm"
          className="tbc-column-select"
          value={selectedColumns[field] !== null ? selectedColumns[field] : ""}
          onChange={(e) => {
            const value =
              e.target.value === "" ? null : parseInt(e.target.value);
            setSelectedColumns({ ...selectedColumns, [field]: value });
          }}
        >
          <option value="">-- Skip Column --</option>
          {excelHeaders.map((header, idx) => (
            <option key={idx} value={idx}>
              {header?.substring(0, 50) || `Column ${idx + 1}`}
            </option>
          ))}
        </Form.Select>
        {required && selectedColumns[field] === null && (
          <Form.Text className="tbc-required-text">
            This field is required
          </Form.Text>
        )}
      </Form.Group>
    );
  };

  return (
    <>
      <Modal
        show={show}
        onHide={handleClose}
        size="xl"
        centered
        backdrop="static"
        className="tbc-modal"
        dialogClassName="tbc-modal-dialog"
        contentClassName="tbc-modal-content"
      >
        <Modal.Header closeButton className="tbc-modal-header">
          <Modal.Title className="tbc-modal-title">
            <i className="fas fa-layer-group tbc-header-icon"></i>
            Bulk Task Creator
            <Badge bg="info" className="tbc-task-count-badge">
              {tasks.length} Tasks
            </Badge>
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="tbc-modal-body">
          {/* Toolbar */}
          <div className="tbc-toolbar">
            <div className="tbc-toolbar-container">
              <div className="tbc-button-group">
                <Button
                  size="sm"
                  variant="outline-primary"
                  className="tbc-btn tbc-btn-add"
                  onClick={() => addNewTasks(1)}
                >
                  <i className="fas fa-plus tbc-btn-icon"></i> Add Task
                </Button>
                <Button
                  size="sm"
                  variant="outline-success"
                  className="tbc-btn tbc-btn-add-multiple"
                  onClick={() => addNewTasks(5)}
                >
                  <i className="fas fa-plus-circle tbc-btn-icon"></i> Add 5
                </Button>
                <Button
                  size="sm"
                  variant="outline-info"
                  className="tbc-btn tbc-btn-excel"
                  onClick={() => setShowExcelModal(true)}
                >
                  <i className="fas fa-file-excel tbc-btn-icon"></i> Upload from
                  Excel
                </Button>
                <Button
                  size="sm"
                  variant="outline-danger"
                  className="tbc-btn tbc-btn-clean"
                  onClick={() => {
                    if (window.confirm("Remove all empty tasks?")) {
                      setTasks(tasks.filter((t) => t.taskName?.trim()));
                    }
                  }}
                >
                  <i className="fas fa-trash tbc-btn-icon"></i> Clean Empty
                </Button>
              </div>

              <div className="tbc-button-group">
                <Button
                  size="sm"
                  variant={
                    selectedTaskIndices.size > 0
                      ? "primary"
                      : "outline-secondary"
                  }
                  className="tbc-btn tbc-btn-select"
                  onClick={selectAllTasks}
                >
                  <i className="fas fa-check-square tbc-btn-icon"></i>
                  {selectedTaskIndices.size === tasks.length
                    ? "Deselect All"
                    : "Select All"}
                </Button>
                {selectedTaskIndices.size > 0 && (
                  <Button
                    size="sm"
                    variant="warning"
                    className="tbc-btn tbc-btn-bulk-edit"
                    onClick={() => setBulkEditMode(true)}
                  >
                    <i className="fas fa-edit tbc-btn-icon"></i>
                    Bulk Edit ({selectedTaskIndices.size})
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* Bulk Edit Panel */}
          {bulkEditMode && (
            <Alert variant="info" className="tbc-bulk-edit-panel">
              <Alert.Heading className="tbc-bulk-edit-title">
                Bulk Edit {selectedTaskIndices.size} Tasks
              </Alert.Heading>
              <Row className="tbc-bulk-edit-row">
                <Col md={3} className="tbc-bulk-edit-col">
                  <Form.Select
                    size="sm"
                    className="tbc-bulk-select"
                    value={bulkValues.taskPriority || ""}
                    onChange={(e) =>
                      setBulkValues({
                        ...bulkValues,
                        taskPriority: e.target.value,
                      })
                    }
                  >
                    <option value="">Set Priority...</option>
                    {priorities.map((p) => (
                      <option key={p.value} value={p.value}>
                        {p.label}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
                <Col md={3} className="tbc-bulk-edit-col">
                  <Form.Select
                    size="sm"
                    className="tbc-bulk-select"
                    value={bulkValues.taskStatus || ""}
                    onChange={(e) =>
                      setBulkValues({
                        ...bulkValues,
                        taskStatus: e.target.value,
                      })
                    }
                  >
                    <option value="">Set Status...</option>
                    {statusList.map((s) => (
                      <option key={s.statusGuid} value={s.statusGuid}>
                        {s.statusName}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
                <Col md={3} className="tbc-bulk-edit-col">
                  <Form.Select
                    size="sm"
                    className="tbc-bulk-select"
                    value={bulkValues.taskType || ""}
                    onChange={(e) =>
                      setBulkValues({ ...bulkValues, taskType: e.target.value })
                    }
                  >
                    <option value="">Set Task Type...</option>
                    {taskTypes.map((t) => (
                      <option key={t.typeGuid} value={t.typeGuid}>
                        {t.typeName}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
                <Col md={3} className="tbc-bulk-edit-col">
                  <Form.Select
                    size="sm"
                    className="tbc-bulk-select"
                    value={bulkValues.assignedTo || ""}
                    onChange={(e) =>
                      setBulkValues({
                        ...bulkValues,
                        assignedTo: e.target.value,
                      })
                    }
                  >
                    <option value="">Assign To...</option>
                    <option value="unassigned">Unassigned</option>
                    {assignedToOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
              </Row>
              <Row className="tbc-bulk-edit-row tbc-bulk-edit-second-row">
                <Col md={3} className="tbc-bulk-edit-col">
                  <Form.Select
                    size="sm"
                    className="tbc-bulk-select"
                    value={bulkValues.stageBelong || ""}
                    onChange={(e) =>
                      setBulkValues({
                        ...bulkValues,
                        stageBelong: e.target.value,
                      })
                    }
                  >
                    <option value="">Set Stage...</option>
                    {stages.map((s) => (
                      <option key={s.stageId} value={s.stageId}>
                        {s.stageName}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
                <Col md={3} className="tbc-bulk-edit-col">
                  <div className="tbc-bulk-actions">
                    <Button
                      size="sm"
                      variant="primary"
                      className="tbc-bulk-apply-btn"
                      onClick={applyBulkUpdate}
                    >
                      Apply
                    </Button>
                    <Button
                      size="sm"
                      variant="secondary"
                      className="tbc-bulk-cancel-btn"
                      onClick={() => setBulkEditMode(false)}
                    >
                      Cancel
                    </Button>
                  </div>
                </Col>
              </Row>
            </Alert>
          )}

          {/* Tasks Table */}
          <div className="tbc-table-container">
            <Table bordered hover className="tbc-tasks-table">
              <thead className="tbc-table-header">
                <tr>
                  <th className="tbc-checkbox-col">
                    <Form.Check
                      type="checkbox"
                      checked={
                        selectedTaskIndices.size === tasks.length &&
                        tasks.length > 0
                      }
                      onChange={selectAllTasks}
                    />
                  </th>
                  <th className="tbc-sno-col">#</th>
                  <th className="tbc-taskname-col">Task Name *</th>
                  <th className="tbc-description-col">Description</th>
                  <th className="tbc-assignee-col">Assignee</th>
                  <th className="tbc-stage-col">Stage</th>
                  <th className="tbc-startdate-col">Start Date</th>
                  <th className="tbc-enddate-col">End Date</th>
                  <th className="tbc-priority-col">Priority</th>
                  <th className="tbc-status-col">Status</th>
                  <th className="tbc-type-col">Task Type</th>
                  <th className="tbc-actions-col">Actions</th>
                </tr>
              </thead>
              <tbody className="tbc-table-body">
                {tasks.map((task, index) => (
                  <tr
                    key={task.id}
                    className={
                      task.errors.taskName ? "tbc-error-row" : "tbc-normal-row"
                    }
                  >
                    <td className="tbc-checkbox-cell">
                      <Form.Check
                        type="checkbox"
                        checked={selectedTaskIndices.has(index)}
                        onChange={() => toggleTaskSelection(index)}
                      />
                    </td>
                    <td className="tbc-sno-cell">{index + 1}</td>
                    <td className="tbc-taskname-cell">
                      <Form.Control
                        type="text"
                        size="sm"
                        className="tbc-taskname-input"
                        value={task.taskName}
                        onChange={(e) =>
                          updateTask(index, "taskName", e.target.value)
                        }
                        placeholder="Enter task name"
                        isInvalid={!!task.errors.taskName}
                      />
                      {task.errors.taskName && (
                        <Form.Control.Feedback
                          type="invalid"
                          className="tbc-error-feedback"
                        >
                          {task.errors.taskName}
                        </Form.Control.Feedback>
                      )}
                    </td>
                    <td className="tbc-description-cell">
                      <Form.Control
                        type="text"
                        size="sm"
                        className="tbc-description-input"
                        value={task.taskDescription}
                        onChange={(e) =>
                          updateTask(index, "taskDescription", e.target.value)
                        }
                        placeholder="Optional description"
                      />
                    </td>
                    <td className="tbc-assignee-cell">
                      <Form.Select
                        size="sm"
                        className="tbc-assignee-select"
                        value={task.assignedTo || ""}
                        onChange={(e) =>
                          updateTask(index, "assignedTo", e.target.value)
                        }
                        disabled={loadingAssignedTo}
                      >
                        <option value="">Unassigned</option>
                        {assignedToOptions.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </Form.Select>
                    </td>
                    <td className="tbc-stage-cell">
                      <Form.Select
                        size="sm"
                        className="tbc-stage-select"
                        value={task.stageBelong || ""}
                        onChange={(e) =>
                          updateTask(index, "stageBelong", e.target.value)
                        }
                        disabled={loadingStages}
                      >
                        <option value="">Select Stage</option>
                        {stages.map((s) => (
                          <option key={s.stageId} value={s.stageId}>
                            {s.stageName}
                          </option>
                        ))}
                      </Form.Select>
                    </td>
                    <td className="tbc-startdate-cell">
                      <Form.Control
                        type="date"
                        size="sm"
                        className="tbc-date-input"
                        value={task.taskStartDate}
                        onChange={(e) =>
                          updateTask(index, "taskStartDate", e.target.value)
                        }
                      />
                    </td>
                    <td className="tbc-enddate-cell">
                      <Form.Control
                        type="date"
                        size="sm"
                        className="tbc-date-input"
                        value={task.taskEndDate}
                        onChange={(e) =>
                          updateTask(index, "taskEndDate", e.target.value)
                        }
                        isInvalid={!!task.errors.taskEndDate}
                      />
                      {task.errors.taskEndDate && (
                        <Form.Control.Feedback
                          type="invalid"
                          className="tbc-error-feedback"
                        >
                          {task.errors.taskEndDate}
                        </Form.Control.Feedback>
                      )}
                    </td>
                    <td className="tbc-priority-cell">
                      <Form.Select
                        size="sm"
                        className="tbc-priority-select"
                        value={task.taskPriority}
                        onChange={(e) =>
                          updateTask(index, "taskPriority", e.target.value)
                        }
                      >
                        {priorities.map((p) => (
                          <option key={p.value} value={p.value}>
                            {p.label}
                          </option>
                        ))}
                      </Form.Select>
                    </td>
                    <td className="tbc-status-cell">
                      <Form.Select
                        size="sm"
                        className="tbc-status-select"
                        value={task.taskStatus || ""}
                        onChange={(e) =>
                          updateTask(index, "taskStatus", e.target.value)
                        }
                        disabled={loadingStatus}
                      >
                        <option value="">Select Status</option>
                        {statusList.map((s) => (
                          <option key={s.statusGuid} value={s.statusGuid}>
                            {s.statusName}
                          </option>
                        ))}
                      </Form.Select>
                    </td>
                    <td className="tbc-type-cell">
                      <Form.Select
                        size="sm"
                        className="tbc-type-select"
                        value={task.taskType || ""}
                        onChange={(e) =>
                          updateTask(index, "taskType", e.target.value)
                        }
                        disabled={loadingTaskTypes}
                      >
                        <option value="">Select Type</option>
                        {taskTypes.map((t) => (
                          <option key={t.typeGuid} value={t.typeGuid}>
                            {t.typeName}
                          </option>
                        ))}
                      </Form.Select>
                    </td>
                    <td className="tbc-actions-cell">
                      <Button
                        variant="link"
                        size="sm"
                        className="tbc-delete-btn"
                        onClick={() => removeTask(index)}
                      >
                        <i className="fas fa-trash"></i>
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>

          {/* Quick Actions */}
          <div className="tbc-quick-actions">
            <div className="tbc-quick-actions-container">
              <Button
                size="sm"
                variant="outline-secondary"
                className="tbc-template-btn"
                onClick={() => {
                  const template = {
                    taskName: "New Task",
                    taskDescription: "",
                    assignedTo: "",
                    taskStartDate: moment().format("YYYY-MM-DD"),
                    taskEndDate: moment().add(7, "days").format("YYYY-MM-DD"),
                    taskPriority: "Medium",
                    taskStatus: statusList[0]?.statusGuid || "",
                    taskType: taskTypes[0]?.typeGuid || "",
                    stageBelong: stages[0]?.stageId || "",
                  };
                  setTasks((prev) => [
                    ...prev,
                    { ...template, id: `temp_${Date.now()}` },
                  ]);
                }}
              >
                <i className="fas fa-file-alt tbc-btn-icon"></i> Add from
                Template
              </Button>

              <Form.Check
                type="switch"
                label="Detect Duplicates"
                checked={duplicateDetection}
                onChange={(e) => setDuplicateDetection(e.target.checked)}
                className="tbc-duplicate-switch"
              />
            </div>
          </div>

          {/* Validation Summary */}
          {showValidation &&
            tasks.some((t) => Object.keys(t.errors).length > 0) && (
              <Alert variant="danger" className="tbc-validation-alert">
                <i className="fas fa-exclamation-triangle tbc-alert-icon"></i>
                <strong className="tbc-alert-title">
                  Validation Errors Found:
                </strong>
                <ul className="tbc-error-list">
                  {tasks.map((task, idx) =>
                    Object.entries(task.errors).map(
                      ([field, error]) =>
                        error && (
                          <li
                            key={`${idx}-${field}`}
                            className="tbc-error-item"
                          >
                            Task {idx + 1}: {error}
                          </li>
                        ),
                    ),
                  )}
                </ul>
              </Alert>
            )}
        </Modal.Body>

        <Modal.Footer className="tbc-modal-footer">
          <div className="tbc-footer-info">
            <span className="tbc-info-text">
              <i className="fas fa-info-circle tbc-info-icon"></i>
              Select multiple tasks for bulk updates | 📊 Upload Excel files
            </span>
          </div>
          <div className="tbc-footer-buttons">
            <Button
              variant="secondary"
              className="tbc-cancel-btn"
              onClick={handleClose}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              className="tbc-save-btn"
              onClick={handleSave}
              disabled={
                saving || tasks.filter((t) => t.taskName?.trim()).length === 0
              }
            >
              {saving ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    className="tbc-spinner"
                  />
                  Creating...
                </>
              ) : (
                <>
                  <i className="fas fa-save tbc-btn-icon"></i>
                  Create {tasks.filter((t) => t.taskName?.trim()).length} Tasks
                </>
              )}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      {/* Upload Excel Modal with Sheet Selection */}
      <Modal
        show={showExcelModal}
        onHide={() => {
          setShowExcelModal(false);
          setExcelData([]);
          setExcelHeaders([]);
          setSelectedFile(null);
          setAvailableSheets([]);
          setSelectedSheet("");
          setWorkbookData(null);
          if (fileInputRef.current) {
            fileInputRef.current.value = "";
          }
        }}
        size="lg"
        centered
        className="tbc-excel-modal"
        dialogClassName="tbc-excel-dialog"
      >
        <Modal.Header closeButton className="tbc-excel-header">
          <Modal.Title className="tbc-excel-title">
            <i className="fas fa-file-excel tbc-excel-icon"></i>
            Upload Tasks from Excel
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="tbc-excel-body">
          <Alert variant="info" className="tbc-excel-info">
            <strong className="tbc-excel-info-title">How to use:</strong>
            <ol className="tbc-excel-steps">
              <li>Prepare your Excel file (.xlsx, .xls, or .csv)</li>
              <li>First row should contain column headers</li>
              <li>Each subsequent row represents one task</li>
              <li>Upload the file and select the appropriate sheet</li>
              <li>Map columns to task fields (Task Name is required)</li>
              <li>Click "Add Tasks" to import</li>
            </ol>
          </Alert>

          <div className="tbc-upload-area">
            <Form.Group className="tbc-upload-group">
              <Form.Label className="tbc-upload-label">
                <i className="fas fa-cloud-upload-alt"></i> Select Excel File:
              </Form.Label>
              <Form.Control
                type="file"
                ref={fileInputRef}
                accept=".xlsx,.xls,.csv"
                onChange={handleFileUpload}
                className="tbc-file-input"
                disabled={uploading}
              />
              {uploading && (
                <div className="tbc-uploading-indicator">
                  <Spinner animation="border" size="sm" />
                  <span className="ms-2">Uploading and parsing file...</span>
                </div>
              )}
              {selectedFile && !uploading && excelData.length > 0 && (
                <div className="tbc-file-info">
                  <i className="fas fa-check-circle text-success"></i>
                  <span className="ms-2">Loaded: {selectedFile.name}</span>
                </div>
              )}
            </Form.Group>
          </div>

          {/* Sheet Selection Dropdown */}
          {availableSheets.length > 1 && (
            <Form.Group className="tbc-sheet-select-group">
              <Form.Label className="tbc-sheet-select-label">
                <i className="fas fa-table me-2"></i>Select Worksheet:
              </Form.Label>
              <Form.Select
                value={selectedSheet}
                onChange={(e) => handleSheetChange(e.target.value)}
                className="tbc-sheet-select"
                disabled={uploading}
              >
                {availableSheets.map((sheet, idx) => (
                  <option key={idx} value={sheet}>
                    {sheet}{" "}
                    {sheet === selectedSheet && excelData.length > 0
                      ? `(${excelData.length} tasks)`
                      : ""}
                  </option>
                ))}
              </Form.Select>
              {selectedSheet && excelData.length > 0 && (
                <Form.Text className="tbc-sheet-info">
                  <i className="fas fa-info-circle"></i> Showing{" "}
                  {excelData.length} tasks from sheet "{selectedSheet}"
                </Form.Text>
              )}
            </Form.Group>
          )}

          {excelHeaders.length > 0 && excelData.length > 0 && (
            <>
              <div className="tbc-column-mapping">
                <h6 className="tbc-mapping-title">
                  Step 2: Map Columns to Task Fields
                </h6>
                <Alert variant="warning" className="tbc-mapping-warning">
                  <small>
                    <i className="fas fa-info-circle tbc-warning-icon"></i>
                    <strong>Note:</strong> "Task Name" is required. Select "Skip
                    Column" for columns you don't want to import.
                  </small>
                </Alert>
                <Row className="tbc-mapping-row">
                  <Col md={6} className="tbc-mapping-col">
                    {renderColumnSelect("taskName", "Task Name", true)}
                  </Col>
                  <Col md={6} className="tbc-mapping-col">
                    {renderColumnSelect(
                      "taskDescription",
                      "Description",
                      false,
                    )}
                  </Col>
                  <Col md={6} className="tbc-mapping-col">
                    {renderColumnSelect("assignedTo", "Assignee", false)}
                  </Col>
                  <Col md={6} className="tbc-mapping-col">
                    {renderColumnSelect("taskStartDate", "Start Date", false)}
                  </Col>
                  <Col md={6} className="tbc-mapping-col">
                    {renderColumnSelect("taskEndDate", "End Date", false)}
                  </Col>
                  <Col md={6} className="tbc-mapping-col">
                    {renderColumnSelect("taskPriority", "Priority", false)}
                  </Col>
                  <Col md={6} className="tbc-mapping-col">
                    {renderColumnSelect("taskStatus", "Status", false)}
                  </Col>
                  <Col md={6} className="tbc-mapping-col">
                    {renderColumnSelect("taskType", "Task Type", false)}
                  </Col>
                  <Col md={6} className="tbc-mapping-col">
                    {renderColumnSelect("stageBelong", "Stage", false)}
                  </Col>
                </Row>
              </div>

              <div className="tbc-preview-section">
                <h6 className="tbc-preview-title">Preview (First 5 rows):</h6>
                <div className="tbc-preview-table-container">
                  <Table size="sm" bordered className="tbc-preview-table">
                    <thead className="tbc-preview-header">
                      <tr>
                        <th>Task Name</th>
                        <th>Description</th>
                        <th>Assignee</th>
                        <th>Start Date</th>
                        <th>End Date</th>
                        <th>Priority</th>
                        <th>Status</th>
                        <th>Task Type</th>
                        <th>Stage</th>
                      </tr>
                    </thead>
                    <tbody className="tbc-preview-body">
                      {excelData.slice(0, 5).map((row, idx) => (
                        <tr key={idx} className="tbc-preview-row">
                          <td className="tbc-preview-cell">
                            {selectedColumns.taskName !== null
                              ? row[selectedColumns.taskName] || "-"
                              : "-"}
                          </td>
                          <td className="tbc-preview-cell">
                            {selectedColumns.taskDescription !== null
                              ? row[selectedColumns.taskDescription] || "-"
                              : "-"}
                          </td>
                          <td className="tbc-preview-cell">
                            {selectedColumns.assignedTo !== null
                              ? row[selectedColumns.assignedTo] || "-"
                              : "-"}
                          </td>
                          <td className="tbc-preview-cell">
                            {selectedColumns.taskStartDate !== null
                              ? row[selectedColumns.taskStartDate] || "-"
                              : "-"}
                          </td>
                          <td className="tbc-preview-cell">
                            {selectedColumns.taskEndDate !== null
                              ? row[selectedColumns.taskEndDate] || "-"
                              : "-"}
                          </td>
                          <td className="tbc-preview-cell">
                            {selectedColumns.taskPriority !== null
                              ? row[selectedColumns.taskPriority] || "Medium"
                              : "Medium"}
                          </td>
                          <td className="tbc-preview-cell">
                            {selectedColumns.taskStatus !== null
                              ? row[selectedColumns.taskStatus] || "Not Started"
                              : "Not Started"}
                          </td>
                          <td className="tbc-preview-cell">
                            {selectedColumns.taskType !== null
                              ? row[selectedColumns.taskType] || "-"
                              : "-"}
                          </td>
                          <td className="tbc-preview-cell">
                            {selectedColumns.stageBelong !== null
                              ? row[selectedColumns.stageBelong] || "-"
                              : "-"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
                <small className="tbc-preview-info">
                  Total valid tasks: {excelData.length} | File:{" "}
                  {selectedFile?.name} | Sheet: {selectedSheet}
                </small>
              </div>
            </>
          )}

          {excelHeaders.length === 0 && selectedFile && !uploading && (
            <Alert variant="warning" className="mt-3">
              <i className="fas fa-exclamation-triangle me-2"></i>
              No valid data found in the selected sheet. Please check that your
              Excel file has headers and data rows.
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer className="tbc-excel-footer">
          <Button
            variant="secondary"
            className="tbc-excel-cancel-btn"
            onClick={() => {
              setShowExcelModal(false);
              setExcelData([]);
              setExcelHeaders([]);
              setSelectedFile(null);
              setAvailableSheets([]);
              setSelectedSheet("");
              setWorkbookData(null);
              if (fileInputRef.current) {
                fileInputRef.current.value = "";
              }
            }}
          >
            Cancel
          </Button>
          <Button
            variant="success"
            className="tbc-excel-add-btn"
            onClick={addTasksFromExcel}
            disabled={
              excelData.length === 0 || selectedColumns.taskName === null
            }
          >
            <i className="fas fa-plus tbc-btn-icon"></i>
            Add {excelData.length} Tasks
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default TaskBatchCreator;

// // TaskBatchCreator.jsx - With Excel Upload instead of Paste
// import React, { useState, useEffect, useRef } from "react";
// import {
//   Modal,
//   Button,
//   Form,
//   Row,
//   Col,
//   Table,
//   Badge,
//   Alert,
//   Spinner,
// } from "react-bootstrap";
// import { toast } from "react-toastify";
// import moment from "moment";
// import * as XLSX from 'xlsx'; // Add this import
// import { saveTaskDetails, getContactGuid } from "../../Api/TaskMaster";
// import { getAllTaskStatus, getAllTaskTypes } from "../../Api/TaskMaster";
// import { ticketDropdownApi } from "../../Api/TicketApiOrg";
// import { fetchStagesByProject } from "../../Api/StageMaster";
// import "./TaskBatchCreator.css";

// const TaskBatchCreator = ({
//   show,
//   onHide,
//   projectId,
//   orgId,
//   userId,
//   teamMembers,
//   onSuccess,
//   existingTasks = [],
// }) => {
//   const [tasks, setTasks] = useState([]);
//   const [selectedTaskIndices, setSelectedTaskIndices] = useState(new Set());
//   const [bulkEditMode, setBulkEditMode] = useState(false);
//   const [bulkValues, setBulkValues] = useState({});
//   const [saving, setSaving] = useState(false);
//   const [showValidation, setShowValidation] = useState(false);
//   const [duplicateDetection, setDuplicateDetection] = useState(true);
//   const [showExcelModal, setShowExcelModal] = useState(false);
//   const [excelData, setExcelData] = useState([]);
//   const [excelHeaders, setExcelHeaders] = useState([]);
//   const [selectedFile, setSelectedFile] = useState(null);
//   const [uploading, setUploading] = useState(false);

//   // Dynamic data states (same as TaskMaster)
//   const [assignedToOptions, setAssignedToOptions] = useState([]);
//   const [loadingAssignedTo, setLoadingAssignedTo] = useState(false);
//   const [taskTypes, setTaskTypes] = useState([]);
//   const [loadingTaskTypes, setLoadingTaskTypes] = useState(false);
//   const [statusList, setStatusList] = useState([]);
//   const [loadingStatus, setLoadingStatus] = useState(false);
//   const [stages, setStages] = useState([]);
//   const [loadingStages, setLoadingStages] = useState(false);

//   const [selectedColumns, setSelectedColumns] = useState({
//     taskName: null,
//     taskDescription: null,
//     assignedTo: null,
//     taskStartDate: null,
//     taskEndDate: null,
//     taskPriority: null,
//     taskStatus: null,
//     taskType: null,
//     stageBelong: null,
//   });

//   const hasInitialized = useRef(false);
//   const fileInputRef = useRef(null);
//   const username = JSON.parse(localStorage.getItem("userDetails"))?.username || "";

//   // Priority options (hardcoded as they're standard)
//   const priorities = [
//     { value: "Low", label: "Low" },
//     { value: "Medium", label: "Medium" },
//     { value: "High", label: "High" },
//     { value: "Critical", label: "Critical" }
//   ];

//   // Load assignedTo options from ticketDropdownApi
//   const loadAssignedToOptions = async () => {
//     if (!orgId || !projectId) {
//       setAssignedToOptions([]);
//       return;
//     }

//     setLoadingAssignedTo(true);
//     try {
//       const [team, customers, partners] = await Promise.all([
//         ticketDropdownApi.getTeamAssigned(orgId, projectId),
//         ticketDropdownApi.getCustomer(orgId, projectId),
//         ticketDropdownApi.getPartner(orgId, projectId),
//       ]);

//       const assigneeMap = new Map();

//       team.forEach((item) => {
//         if (!assigneeMap.has(item.contactGUID)) {
//           assigneeMap.set(item.contactGUID, {
//             value: item.contactGUID,
//             label: `${item.contactName} (Team)`,
//             type: "Team",
//             contactName: item.contactName,
//           });
//         }
//       });

//       customers.forEach((item) => {
//         if (!assigneeMap.has(item.contactGUID)) {
//           assigneeMap.set(item.contactGUID, {
//             value: item.contactGUID,
//             label: `${item.contactName} (Customer)`,
//             type: "Customer",
//             contactName: item.contactName,
//           });
//         }
//       });

//       partners.forEach((item) => {
//         if (!assigneeMap.has(item.contactGUID)) {
//           assigneeMap.set(item.contactGUID, {
//             value: item.contactGUID,
//             label: `${item.contactName} (Partner)`,
//             type: "Partner",
//             contactName: item.contactName,
//           });
//         }
//       });

//       const sortedOptions = Array.from(assigneeMap.values()).sort((a, b) =>
//         a.label.localeCompare(b.label)
//       );

//       setAssignedToOptions(sortedOptions);
//     } catch (error) {
//       console.error("Error loading assigned to options:", error);
//       setAssignedToOptions([]);
//     } finally {
//       setLoadingAssignedTo(false);
//     }
//   };

//   // Load task types from API
//   const loadTaskTypes = async () => {
//     try {
//       setLoadingTaskTypes(true);
//       const data = await getAllTaskTypes();
//       const sorted = data.sort((a, b) => a.typeName.localeCompare(b.typeName));
//       setTaskTypes(sorted);
//     } catch (error) {
//       console.error("Error loading task types:", error);
//       setTaskTypes([]);
//     } finally {
//       setLoadingTaskTypes(false);
//     }
//   };

//   // Load task statuses from API
//   const loadTaskStatus = async () => {
//     try {
//       setLoadingStatus(true);
//       const data = await getAllTaskStatus();
//       const sorted = data.sort((a, b) => a.statusName.localeCompare(b.statusName));
//       setStatusList(sorted);
//     } catch (error) {
//       console.error("Error loading status:", error);
//       setStatusList([]);
//     } finally {
//       setLoadingStatus(false);
//     }
//   };

//   // Load stages
//   const loadStages = async () => {
//     if (!projectId) {
//       setStages([]);
//       return;
//     }

//     setLoadingStages(true);
//     try {
//       const stagesData = await fetchStagesByProject(projectId);
//       console.log("Loaded stages:", stagesData);
//       setStages(stagesData || []);
//     } catch (error) {
//       console.error("Error loading stages:", error);
//       setStages([]);
//     } finally {
//       setLoadingStages(false);
//     }
//   };

//   // Load all dynamic data when modal opens
//   useEffect(() => {
//     if (show && projectId) {
//       loadAssignedToOptions();
//       loadTaskTypes();
//       loadTaskStatus();
//       loadStages();
//     }
//   }, [show, projectId, orgId]);

//   const handleClose = () => {
//     setTasks([]);
//     setSelectedTaskIndices(new Set());
//     setBulkEditMode(false);
//     setBulkValues({});
//     setShowValidation(false);
//     setShowExcelModal(false);
//     setExcelData([]);
//     setExcelHeaders([]);
//     setSelectedFile(null);
//     setSelectedColumns({
//       taskName: null,
//       taskDescription: null,
//       assignedTo: null,
//       taskStartDate: null,
//       taskEndDate: null,
//       taskPriority: null,
//       taskStatus: null,
//       taskType: null,
//       stageBelong: null,
//     });
//     hasInitialized.current = false;
//     onHide();
//   };

//   useEffect(() => {
//     if (show && !hasInitialized.current) {
//       hasInitialized.current = true;
//       if (existingTasks.length > 0) {
//         const clonedTasks = existingTasks.map((task) => ({
//           id: `temp_${Date.now()}_${Math.random()}`,
//           taskName: `${task.taskName} (Copy)`,
//           taskDescription: task.taskDescription || "",
//           assignedTo: task.assignedTo || "",
//           taskStartDate: task.taskStartDate || "",
//           taskEndDate: task.taskEndDate || "",
//           taskPriority: task.taskPriority || "Medium",
//           taskStatus: task.taskStatus || "",
//           taskType: task.taskType || "",
//           stageBelong: task.stageBelong || "",
//           errors: {},
//         }));
//         setTasks(clonedTasks);
//       } else {
//         addNewTasks(3);
//       }
//     }
//     if (!show) {
//       hasInitialized.current = false;
//     }
//   }, [show, existingTasks]);

//   // Handle Excel file upload
//   const handleFileUpload = async (event) => {
//     const file = event.target.files[0];
//     if (!file) return;

//     // Check file type
//     const validTypes = [
//       'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
//       'application/vnd.ms-excel',
//       'text/csv'
//     ];

//     if (!validTypes.includes(file.type) && !file.name.match(/\.(xlsx|xls|csv)$/)) {
//       toast.error("Please upload a valid Excel file (.xlsx, .xls, or .csv)");
//       return;
//     }

//     setUploading(true);
//     setSelectedFile(file);

//     try {
//       const data = await readExcelFile(file);
//       if (data && data.length > 0) {
//         const headers = data[0];
//         const rows = data.slice(1);

//         setExcelHeaders(headers);
//         setExcelData(rows);

//         // Auto-detect column mappings based on header names
//         const autoMapped = detectColumnMappings(headers);
//         setSelectedColumns(autoMapped);

//         toast.success(`Successfully loaded ${rows.length} rows from ${file.name}`);
//       } else {
//         toast.error("No data found in the file");
//       }
//     } catch (error) {
//       console.error("Error reading file:", error);
//       toast.error("Failed to read the Excel file");
//     } finally {
//       setUploading(false);
//     }
//   };

//   // Read Excel file
//   const readExcelFile = (file) => {
//     return new Promise((resolve, reject) => {
//       const reader = new FileReader();

//       reader.onload = (e) => {
//         try {
//           const data = new Uint8Array(e.target.result);
//           const workbook = XLSX.read(data, { type: 'array' });
//           const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
//           const jsonData = XLSX.utils.sheet_to_json(firstSheet, { header: 1, defval: "" });

//           // Filter out empty rows
//           const nonEmptyRows = jsonData.filter(row =>
//             row && row.some(cell => cell && cell.toString().trim() !== "")
//           );

//           resolve(nonEmptyRows);
//         } catch (error) {
//           reject(error);
//         }
//       };

//       reader.onerror = (error) => reject(error);
//       reader.readAsArrayBuffer(file);
//     });
//   };

//   // Auto-detect column mappings based on header names
//   const detectColumnMappings = (headers) => {
//     const mappings = {};
//     const headerLower = headers.map(h => (h || "").toString().toLowerCase());

//     const fieldKeywords = {
//       taskName: ['task', 'name', 'task name', 'title', 'activity', 'work'],
//       taskDescription: ['description', 'desc', 'details', 'notes', 'comments', 'specifications'],
//       assignedTo: ['assignee', 'assigned to', 'assigned', 'owner', 'responsible', 'resource', 'contact'],
//       taskStartDate: ['start', 'start date', 'start date', 'from', 'begin', 'start day'],
//       taskEndDate: ['end', 'end date', 'end date', 'to', 'due', 'due date', 'deadline', 'finish'],
//       taskPriority: ['priority', 'level', 'importance', 'severity'],
//       taskStatus: ['status', 'state', 'progress'],
//       taskType: ['type', 'task type', 'category', 'classification'],
//       stageBelong: ['stage', 'phase', 'sprint', 'milestone']
//     };

//     Object.keys(fieldKeywords).forEach(field => {
//       const keywords = fieldKeywords[field];
//       const matchedIndex = headerLower.findIndex(header =>
//         keywords.some(keyword => header.includes(keyword))
//       );
//       mappings[field] = matchedIndex !== -1 ? matchedIndex : null;
//     });

//     return mappings;
//   };

//   // Add tasks from Excel
//   const addTasksFromExcel = () => {
//     if (excelData.length === 0) return;

//     if (selectedColumns.taskName === null || selectedColumns.taskName === undefined) {
//       toast.error("Please select a column for Task Name (required field)");
//       return;
//     }

//     const newTasks = excelData.map((row, idx) => {
//       const taskName = selectedColumns.taskName !== null ? row[selectedColumns.taskName] || "" : "";
//       const taskDescription = selectedColumns.taskDescription !== null ? row[selectedColumns.taskDescription] || "" : "";
//       let assignedTo = selectedColumns.assignedTo !== null ? row[selectedColumns.assignedTo] || "" : "";
//       let taskStartDate = selectedColumns.taskStartDate !== null ? row[selectedColumns.taskStartDate] || "" : "";
//       let taskEndDate = selectedColumns.taskEndDate !== null ? row[selectedColumns.taskEndDate] || "" : "";
//       let taskPriority = selectedColumns.taskPriority !== null ? row[selectedColumns.taskPriority] || "Medium" : "Medium";
//       let taskStatus = selectedColumns.taskStatus !== null ? row[selectedColumns.taskStatus] || "" : "";
//       let taskType = selectedColumns.taskType !== null ? row[selectedColumns.taskType] || "" : "";
//       let stageBelong = selectedColumns.stageBelong !== null ? row[selectedColumns.stageBelong] || "" : "";

//       if (!taskName) return null;

//       // Format dates if needed
//       if (taskStartDate && !taskStartDate.match(/^\d{4}-\d{2}-\d{2}$/)) {
//         taskStartDate = formatExcelDate(taskStartDate);
//       }
//       if (taskEndDate && !taskEndDate.match(/^\d{4}-\d{2}-\d{2}$/)) {
//         taskEndDate = formatExcelDate(taskEndDate);
//       }

//       // Try to find matching assignee by name
//       if (assignedTo && assignedToOptions.length > 0) {
//         const matchedOption = assignedToOptions.find(opt =>
//           opt.contactName?.toLowerCase() === assignedTo.toLowerCase() ||
//           opt.label?.toLowerCase().includes(assignedTo.toLowerCase())
//         );
//         if (matchedOption) {
//           assignedTo = matchedOption.value;
//         }
//       }

//       // Try to find matching task type by name
//       if (taskType && taskTypes.length > 0) {
//         const matchedType = taskTypes.find(t =>
//           t.typeName?.toLowerCase() === taskType.toLowerCase()
//         );
//         if (matchedType) {
//           taskType = matchedType.typeGuid;
//         }
//       }

//       // Try to find matching status by name
//       if (taskStatus && statusList.length > 0) {
//         const matchedStatus = statusList.find(s =>
//           s.statusName?.toLowerCase() === taskStatus.toLowerCase()
//         );
//         if (matchedStatus) {
//           taskStatus = matchedStatus.statusGuid;
//         }
//       }

//       // Try to find matching stage by name
//       if (stageBelong && stages.length > 0) {
//         const matchedStage = stages.find(s =>
//           s.stageName?.toLowerCase() === stageBelong.toLowerCase()
//         );
//         if (matchedStage) {
//           stageBelong = matchedStage.stageId;
//         }
//       }

//       // Validate priority
//       const validPriorities = priorities.map(p => p.value);
//       if (!validPriorities.includes(taskPriority)) {
//         taskPriority = "Medium";
//       }

//       return {
//         id: `temp_${Date.now()}_${Math.random()}_${idx}`,
//         taskName: taskName,
//         taskDescription: taskDescription,
//         assignedTo: assignedTo,
//         taskStartDate: taskStartDate,
//         taskEndDate: taskEndDate,
//         taskPriority: taskPriority,
//         taskStatus: taskStatus,
//         taskType: taskType,
//         stageBelong: stageBelong,
//         errors: {},
//       };
//     }).filter(task => task !== null);

//     if (newTasks.length === 0) {
//       toast.error("No valid tasks found. Make sure Task Name column has data.");
//       return;
//     }

//     setTasks(prev => [...prev, ...newTasks]);
//     setShowExcelModal(false);
//     setExcelData([]);
//     setExcelHeaders([]);
//     setSelectedFile(null);
//     if (fileInputRef.current) {
//       fileInputRef.current.value = "";
//     }
//     toast.success(`Added ${newTasks.length} tasks from Excel file`);
//   };

//   // Format Excel date to YYYY-MM-DD
//   const formatExcelDate = (dateValue) => {
//     if (!dateValue) return "";

//     // If it's a number (Excel serial date)
//     if (typeof dateValue === 'number') {
//       const date = XLSX.SSF.parse_date_code(dateValue);
//       if (date) {
//         return `${date.y}-${String(date.m).padStart(2, '0')}-${String(date.d).padStart(2, '0')}`;
//       }
//     }

//     // Try to parse string date
//     try {
//       const parsed = new Date(dateValue);
//       if (!isNaN(parsed.getTime())) {
//         return parsed.toISOString().split('T')[0];
//       }
//     } catch (e) {
//       console.error("Date parsing error:", e);
//     }

//     return dateValue;
//   };

//   const addNewTasks = (count = 1) => {
//     const newTasks = Array(count).fill().map(() => ({
//       id: `temp_${Date.now()}_${Math.random()}`,
//       taskName: "",
//       taskDescription: "",
//       assignedTo: "",
//       taskStartDate: "",
//       taskEndDate: "",
//       taskPriority: "Medium",
//       taskStatus: "",
//       taskType: "",
//       stageBelong: "",
//       errors: {},
//     }));
//     setTasks((prev) => [...prev, ...newTasks]);
//   };

//   const removeTask = (index) => {
//     setTasks((prev) => prev.filter((_, i) => i !== index));
//     setSelectedTaskIndices((prev) => {
//       const newSet = new Set();
//       prev.forEach((i) => {
//         if (i < index) newSet.add(i);
//         else if (i > index) newSet.add(i - 1);
//       });
//       return newSet;
//     });
//   };

//   const updateTask = (index, field, value) => {
//     setTasks((prev) => {
//       const updated = [...prev];
//       updated[index] = {
//         ...updated[index],
//         [field]: value,
//         errors: {
//           ...updated[index].errors,
//           [field]: validateField(field, value, updated[index]),
//         },
//       };
//       return updated;
//     });
//   };

//   const validateField = (field, value, task) => {
//     switch (field) {
//       case "taskName":
//         if (!value || !value.trim()) return "Task name is required";
//         if (duplicateDetection && checkDuplicateName(value, task.id)) {
//           return "Duplicate task name detected";
//         }
//         return null;
//       case "taskStartDate":
//         if (value && task.taskEndDate && new Date(value) > new Date(task.taskEndDate)) {
//           return "Start date cannot be after end date";
//         }
//         return null;
//       case "taskEndDate":
//         if (value && task.taskStartDate && new Date(value) < new Date(task.taskStartDate)) {
//           return "End date cannot be before start date";
//         }
//         return null;
//       default:
//         return null;
//     }
//   };

//   const checkDuplicateName = (name, currentId) => {
//     return tasks.some(
//       (t) => t.id !== currentId && t.taskName?.toLowerCase() === name.toLowerCase()
//     );
//   };

//   const applyBulkUpdate = () => {
//     if (selectedTaskIndices.size === 0) return;

//     setTasks((prev) => {
//       const updated = [...prev];
//       selectedTaskIndices.forEach((index) => {
//         updated[index] = {
//           ...updated[index],
//           ...bulkValues,
//           errors: {
//             ...updated[index].errors,
//             ...Object.keys(bulkValues).reduce((acc, field) => {
//               acc[field] = validateField(field, bulkValues[field], updated[index]);
//               return acc;
//             }, {}),
//           },
//         };
//       });
//       return updated;
//     });

//     setBulkEditMode(false);
//     setBulkValues({});
//     toast.success(`Updated ${selectedTaskIndices.size} tasks`);
//   };

//   const toggleTaskSelection = (index) => {
//     setSelectedTaskIndices((prev) => {
//       const newSet = new Set(prev);
//       if (newSet.has(index)) newSet.delete(index);
//       else newSet.add(index);
//       return newSet;
//     });
//   };

//   const selectAllTasks = () => {
//     if (selectedTaskIndices.size === tasks.length) {
//       setSelectedTaskIndices(new Set());
//     } else {
//       setSelectedTaskIndices(new Set(tasks.map((_, i) => i)));
//     }
//   };

//   const validateAllTasks = () => {
//     let isValid = true;
//     const updatedTasks = [...tasks];

//     updatedTasks.forEach((task, index) => {
//       const errors = {};
//       if (!task.taskName?.trim()) {
//         errors.taskName = "Task name is required";
//         isValid = false;
//       }
//       if (task.taskStartDate && task.taskEndDate) {
//         if (new Date(task.taskStartDate) > new Date(task.taskEndDate)) {
//           errors.taskEndDate = "End date must be after start date";
//           isValid = false;
//         }
//       }
//       updatedTasks[index].errors = errors;
//     });

//     setTasks(updatedTasks);
//     setShowValidation(true);

//     if (!isValid) {
//       toast.error("Please fix validation errors before saving");
//     }
//     return isValid;
//   };

//   const handleSave = async () => {
//     if (!validateAllTasks()) return;

//     setSaving(true);
//     const validTasks = tasks.filter((task) => task.taskName?.trim());

//     try {
//       const contactResponse = await getContactGuid(orgId, userId);

//       if (!contactResponse || !contactResponse.contactGuid) {
//         toast.error("Unable to fetch contact GUID");
//         setSaving(false);
//         return;
//       }

//       const contactGuid = contactResponse.contactGuid;
//       console.log("Fetched Contact GUID:", contactGuid);

//       const createdTasks = [];
//       const errors = [];

//       for (const task of validTasks) {
//         try {
//           const taskData = {
//             orgId: String(orgId),
//             taskName: task.taskName,
//             taskDescription: task.taskDescription || "",
//             assignedTo: task.assignedTo || null,
//             taskStartDate: task.taskStartDate || null,
//             taskEndDate: task.taskEndDate || null,
//             taskPriority: task.taskPriority || "Medium",
//             taskStatus: task.taskStatus || null,
//             taskType: task.taskType || null,
//             stageBelong: task.stageBelong || null,
//             projectBelong: projectId,
//             createdBy: username || String(userId),
//             editedBy: username || String(userId),
//             TaskCreatedGuid: contactGuid,
//           };

//           Object.keys(taskData).forEach(key => {
//             if (taskData[key] === null || taskData[key] === undefined) {
//               delete taskData[key];
//             }
//           });

//           console.log("Creating task with data:", taskData);
//           const response = await saveTaskDetails(taskData, username || userId);
//           createdTasks.push(response);
//         } catch (error) {
//           console.error("Error creating task:", error);
//           errors.push({
//             task: task.taskName,
//             error: error.response?.data?.title || error.message
//           });
//         }
//       }

//       if (errors.length > 0) {
//         toast.warning(`Created ${createdTasks.length} tasks, ${errors.length} failed`);
//         console.error("Failed tasks:", errors);
//       } else {
//         toast.success(`Successfully created ${createdTasks.length} tasks`);
//       }

//       onSuccess?.(createdTasks);
//       handleClose();
//     } catch (error) {
//       console.error("Error saving tasks:", error);
//       toast.error(error.response?.data?.title || "Failed to save tasks");
//     } finally {
//       setSaving(false);
//     }
//   };

//   const renderColumnSelect = (field, label, required = false) => {
//     return (
//       <Form.Group className="tbc-column-select-group">
//         <Form.Label className="tbc-column-select-label">
//           {label} {required && <span className="tbc-required-star">*</span>}
//         </Form.Label>
//         <Form.Select
//           size="sm"
//           className="tbc-column-select"
//           value={selectedColumns[field] !== null ? selectedColumns[field] : ""}
//           onChange={(e) => {
//             const value = e.target.value === "" ? null : parseInt(e.target.value);
//             setSelectedColumns({...selectedColumns, [field]: value});
//           }}
//         >
//           <option value="">-- Skip Column --</option>
//           {excelHeaders.map((header, idx) => (
//             <option key={idx} value={idx}>
//               Column {idx + 1}: {header?.substring(0, 50) || `Column ${idx + 1}`}
//             </option>
//           ))}
//         </Form.Select>
//         {required && selectedColumns[field] === null && (
//           <Form.Text className="tbc-required-text">
//             This field is required
//           </Form.Text>
//         )}
//       </Form.Group>
//     );
//   };

//   return (
//     <>
//       <Modal
//         show={show}
//         onHide={handleClose}
//         size="xl"
//         centered
//         backdrop="static"
//         className="tbc-modal"
//         dialogClassName="tbc-modal-dialog"
//         contentClassName="tbc-modal-content"
//       >
//         <Modal.Header closeButton className="tbc-modal-header">
//           <Modal.Title className="tbc-modal-title">
//             <i className="fas fa-layer-group tbc-header-icon"></i>
//             Bulk Task Creator
//             <Badge bg="info" className="tbc-task-count-badge">
//               {tasks.length} Tasks
//             </Badge>
//           </Modal.Title>
//         </Modal.Header>

//         <Modal.Body className="tbc-modal-body">
//           {/* Toolbar */}
//           <div className="tbc-toolbar">
//             <div className="tbc-toolbar-container">
//               <div className="tbc-button-group">
//                 <Button size="sm" variant="outline-primary" className="tbc-btn tbc-btn-add" onClick={() => addNewTasks(1)}>
//                   <i className="fas fa-plus tbc-btn-icon"></i> Add Task
//                 </Button>
//                 <Button size="sm" variant="outline-success" className="tbc-btn tbc-btn-add-multiple" onClick={() => addNewTasks(5)}>
//                   <i className="fas fa-plus-circle tbc-btn-icon"></i> Add 5
//                 </Button>
//                 <Button size="sm" variant="outline-info" className="tbc-btn tbc-btn-excel" onClick={() => setShowExcelModal(true)}>
//                   <i className="fas fa-file-excel tbc-btn-icon"></i> Upload from Excel
//                 </Button>
//                 <Button size="sm" variant="outline-danger" className="tbc-btn tbc-btn-clean" onClick={() => {
//                   if (window.confirm("Remove all empty tasks?")) {
//                     setTasks(tasks.filter((t) => t.taskName?.trim()));
//                   }
//                 }}>
//                   <i className="fas fa-trash tbc-btn-icon"></i> Clean Empty
//                 </Button>
//               </div>

//               <div className="tbc-button-group">
//                 <Button size="sm" variant={selectedTaskIndices.size > 0 ? "primary" : "outline-secondary"} className="tbc-btn tbc-btn-select" onClick={selectAllTasks}>
//                   <i className="fas fa-check-square tbc-btn-icon"></i>
//                   {selectedTaskIndices.size === tasks.length ? "Deselect All" : "Select All"}
//                 </Button>
//                 {selectedTaskIndices.size > 0 && (
//                   <Button size="sm" variant="warning" className="tbc-btn tbc-btn-bulk-edit" onClick={() => setBulkEditMode(true)}>
//                     <i className="fas fa-edit tbc-btn-icon"></i>
//                     Bulk Edit ({selectedTaskIndices.size})
//                   </Button>
//                 )}
//               </div>
//             </div>
//           </div>

//           {/* Bulk Edit Panel */}
//           {bulkEditMode && (
//             <Alert variant="info" className="tbc-bulk-edit-panel">
//               <Alert.Heading className="tbc-bulk-edit-title">Bulk Edit {selectedTaskIndices.size} Tasks</Alert.Heading>
//               <Row className="tbc-bulk-edit-row">
//                 <Col md={3} className="tbc-bulk-edit-col">
//                   <Form.Select size="sm" className="tbc-bulk-select" value={bulkValues.taskPriority || ""} onChange={(e) =>
//                     setBulkValues({ ...bulkValues, taskPriority: e.target.value })}>
//                     <option value="">Set Priority...</option>
//                     {priorities.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
//                   </Form.Select>
//                 </Col>
//                 <Col md={3} className="tbc-bulk-edit-col">
//                   <Form.Select size="sm" className="tbc-bulk-select" value={bulkValues.taskStatus || ""} onChange={(e) =>
//                     setBulkValues({ ...bulkValues, taskStatus: e.target.value })}>
//                     <option value="">Set Status...</option>
//                     {statusList.map(s => <option key={s.statusGuid} value={s.statusGuid}>{s.statusName}</option>)}
//                   </Form.Select>
//                 </Col>
//                 <Col md={3} className="tbc-bulk-edit-col">
//                   <Form.Select size="sm" className="tbc-bulk-select" value={bulkValues.taskType || ""} onChange={(e) =>
//                     setBulkValues({ ...bulkValues, taskType: e.target.value })}>
//                     <option value="">Set Task Type...</option>
//                     {taskTypes.map(t => <option key={t.typeGuid} value={t.typeGuid}>{t.typeName}</option>)}
//                   </Form.Select>
//                 </Col>
//                 <Col md={3} className="tbc-bulk-edit-col">
//                   <Form.Select size="sm" className="tbc-bulk-select" value={bulkValues.assignedTo || ""} onChange={(e) =>
//                     setBulkValues({ ...bulkValues, assignedTo: e.target.value })}>
//                     <option value="">Assign To...</option>
//                     <option value="unassigned">Unassigned</option>
//                     {assignedToOptions.map(opt => (
//                       <option key={opt.value} value={opt.value}>{opt.label}</option>
//                     ))}
//                   </Form.Select>
//                 </Col>
//               </Row>
//               <Row className="tbc-bulk-edit-row tbc-bulk-edit-second-row">
//                 <Col md={3} className="tbc-bulk-edit-col">
//                   <Form.Select size="sm" className="tbc-bulk-select" value={bulkValues.stageBelong || ""} onChange={(e) =>
//                     setBulkValues({ ...bulkValues, stageBelong: e.target.value })}>
//                     <option value="">Set Stage...</option>
//                     {stages.map(s => <option key={s.stageId} value={s.stageId}>{s.stageName}</option>)}
//                   </Form.Select>
//                 </Col>
//                 <Col md={3} className="tbc-bulk-edit-col">
//                   <div className="tbc-bulk-actions">
//                     <Button size="sm" variant="primary" className="tbc-bulk-apply-btn" onClick={applyBulkUpdate}>Apply</Button>
//                     <Button size="sm" variant="secondary" className="tbc-bulk-cancel-btn" onClick={() => setBulkEditMode(false)}>Cancel</Button>
//                   </div>
//                 </Col>
//               </Row>
//             </Alert>
//           )}

//           {/* Tasks Table */}
//           <div className="tbc-table-container">
//             <Table bordered hover className="tbc-tasks-table">
//               <thead className="tbc-table-header">
//                 <tr>
//                   <th className="tbc-checkbox-col">
//                     <Form.Check type="checkbox" checked={selectedTaskIndices.size === tasks.length && tasks.length > 0} onChange={selectAllTasks} />
//                   </th>
//                   <th className="tbc-sno-col">#</th>
//                   <th className="tbc-taskname-col">Task Name *</th>
//                   <th className="tbc-description-col">Description</th>
//                   <th className="tbc-assignee-col">Assignee</th>
//                   <th className="tbc-stage-col">Stage</th>
//                   <th className="tbc-startdate-col">Start Date</th>
//                   <th className="tbc-enddate-col">End Date</th>
//                   <th className="tbc-priority-col">Priority</th>
//                   <th className="tbc-status-col">Status</th>
//                   <th className="tbc-type-col">Task Type</th>
//                   <th className="tbc-actions-col">Actions</th>
//                 </tr>
//               </thead>
//               <tbody className="tbc-table-body">
//                 {tasks.map((task, index) => (
//                   <tr key={task.id} className={task.errors.taskName ? "tbc-error-row" : "tbc-normal-row"}>
//                     <td className="tbc-checkbox-cell">
//                       <Form.Check type="checkbox" checked={selectedTaskIndices.has(index)} onChange={() => toggleTaskSelection(index)} />
//                     </td>
//                     <td className="tbc-sno-cell">{index + 1}</td>
//                     <td className="tbc-taskname-cell">
//                       <Form.Control
//                         type="text"
//                         size="sm"
//                         className="tbc-taskname-input"
//                         value={task.taskName}
//                         onChange={(e) => updateTask(index, "taskName", e.target.value)}
//                         placeholder="Enter task name"
//                         isInvalid={!!task.errors.taskName}
//                       />
//                       {task.errors.taskName && (
//                         <Form.Control.Feedback type="invalid" className="tbc-error-feedback">
//                           {task.errors.taskName}
//                         </Form.Control.Feedback>
//                       )}
//                     </td>
//                     <td className="tbc-description-cell">
//                       <Form.Control
//                         type="text"
//                         size="sm"
//                         className="tbc-description-input"
//                         value={task.taskDescription}
//                         onChange={(e) => updateTask(index, "taskDescription", e.target.value)}
//                         placeholder="Optional description"
//                       />
//                     </td>
//                     <td className="tbc-assignee-cell">
//                       <Form.Select
//                         size="sm"
//                         className="tbc-assignee-select"
//                         value={task.assignedTo || ""}
//                         onChange={(e) => updateTask(index, "assignedTo", e.target.value)}
//                         disabled={loadingAssignedTo}
//                       >
//                         <option value="">Unassigned</option>
//                         {assignedToOptions.map(opt => (
//                           <option key={opt.value} value={opt.value}>{opt.label}</option>
//                         ))}
//                       </Form.Select>
//                     </td>
//                     <td className="tbc-stage-cell">
//                       <Form.Select
//                         size="sm"
//                         className="tbc-stage-select"
//                         value={task.stageBelong || ""}
//                         onChange={(e) => updateTask(index, "stageBelong", e.target.value)}
//                         disabled={loadingStages}
//                       >
//                         <option value="">Select Stage</option>
//                         {stages.map(s => (
//                           <option key={s.stageId} value={s.stageId}>{s.stageName}</option>
//                         ))}
//                       </Form.Select>
//                     </td>
//                     <td className="tbc-startdate-cell">
//                       <Form.Control
//                         type="date"
//                         size="sm"
//                         className="tbc-date-input"
//                         value={task.taskStartDate}
//                         onChange={(e) => updateTask(index, "taskStartDate", e.target.value)}
//                       />
//                     </td>
//                     <td className="tbc-enddate-cell">
//                       <Form.Control
//                         type="date"
//                         size="sm"
//                         className="tbc-date-input"
//                         value={task.taskEndDate}
//                         onChange={(e) => updateTask(index, "taskEndDate", e.target.value)}
//                         isInvalid={!!task.errors.taskEndDate}
//                       />
//                       {task.errors.taskEndDate && (
//                         <Form.Control.Feedback type="invalid" className="tbc-error-feedback">
//                           {task.errors.taskEndDate}
//                         </Form.Control.Feedback>
//                       )}
//                     </td>
//                     <td className="tbc-priority-cell">
//                       <Form.Select
//                         size="sm"
//                         className="tbc-priority-select"
//                         value={task.taskPriority}
//                         onChange={(e) => updateTask(index, "taskPriority", e.target.value)}
//                       >
//                         {priorities.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
//                       </Form.Select>
//                     </td>
//                     <td className="tbc-status-cell">
//                       <Form.Select
//                         size="sm"
//                         className="tbc-status-select"
//                         value={task.taskStatus || ""}
//                         onChange={(e) => updateTask(index, "taskStatus", e.target.value)}
//                         disabled={loadingStatus}
//                       >
//                         <option value="">Select Status</option>
//                         {statusList.map(s => (
//                           <option key={s.statusGuid} value={s.statusGuid}>{s.statusName}</option>
//                         ))}
//                       </Form.Select>
//                     </td>
//                     <td className="tbc-type-cell">
//                       <Form.Select
//                         size="sm"
//                         className="tbc-type-select"
//                         value={task.taskType || ""}
//                         onChange={(e) => updateTask(index, "taskType", e.target.value)}
//                         disabled={loadingTaskTypes}
//                       >
//                         <option value="">Select Type</option>
//                         {taskTypes.map(t => (
//                           <option key={t.typeGuid} value={t.typeGuid}>{t.typeName}</option>
//                         ))}
//                       </Form.Select>
//                     </td>
//                     <td className="tbc-actions-cell">
//                       <Button variant="link" size="sm" className="tbc-delete-btn" onClick={() => removeTask(index)}>
//                         <i className="fas fa-trash"></i>
//                       </Button>
//                     </td>
//                   </tr>
//                 ))}
//               </tbody>
//             </Table>
//           </div>

//           {/* Quick Actions */}
//           <div className="tbc-quick-actions">
//             <div className="tbc-quick-actions-container">
//               <Button size="sm" variant="outline-secondary" className="tbc-template-btn" onClick={() => {
//                 const template = {
//                   taskName: "New Task",
//                   taskDescription: "",
//                   assignedTo: "",
//                   taskStartDate: moment().format("YYYY-MM-DD"),
//                   taskEndDate: moment().add(7, "days").format("YYYY-MM-DD"),
//                   taskPriority: "Medium",
//                   taskStatus: statusList[0]?.statusGuid || "",
//                   taskType: taskTypes[0]?.typeGuid || "",
//                   stageBelong: stages[0]?.stageId || "",
//                 };
//                 setTasks(prev => [...prev, { ...template, id: `temp_${Date.now()}` }]);
//               }}>
//                 <i className="fas fa-file-alt tbc-btn-icon"></i> Add from Template
//               </Button>

//               <Form.Check
//                 type="switch"
//                 label="Detect Duplicates"
//                 checked={duplicateDetection}
//                 onChange={(e) => setDuplicateDetection(e.target.checked)}
//                 className="tbc-duplicate-switch"
//               />
//             </div>
//           </div>

//           {/* Validation Summary */}
//           {showValidation && tasks.some(t => Object.keys(t.errors).length > 0) && (
//             <Alert variant="danger" className="tbc-validation-alert">
//               <i className="fas fa-exclamation-triangle tbc-alert-icon"></i>
//               <strong className="tbc-alert-title">Validation Errors Found:</strong>
//               <ul className="tbc-error-list">
//                 {tasks.map((task, idx) =>
//                   Object.entries(task.errors).map(([field, error]) =>
//                     error && <li key={`${idx}-${field}`} className="tbc-error-item">Task {idx + 1}: {error}</li>
//                   )
//                 )}
//               </ul>
//             </Alert>
//           )}
//         </Modal.Body>

//         <Modal.Footer className="tbc-modal-footer">
//           <div className="tbc-footer-info">
//             <span className="tbc-info-text">
//               <i className="fas fa-info-circle tbc-info-icon"></i>
//               Select multiple tasks for bulk updates | 📊 Upload Excel files
//             </span>
//           </div>
//           <div className="tbc-footer-buttons">
//             <Button variant="secondary" className="tbc-cancel-btn" onClick={handleClose} disabled={saving}>
//               Cancel
//             </Button>
//             <Button
//               variant="primary"
//               className="tbc-save-btn"
//               onClick={handleSave}
//               disabled={saving || tasks.filter(t => t.taskName?.trim()).length === 0}
//             >
//               {saving ? (
//                 <>
//                   <Spinner as="span" animation="border" size="sm" className="tbc-spinner" />
//                   Creating...
//                 </>
//               ) : (
//                 <>
//                   <i className="fas fa-save tbc-btn-icon"></i>
//                   Create {tasks.filter(t => t.taskName?.trim()).length} Tasks
//                 </>
//               )}
//             </Button>
//           </div>
//         </Modal.Footer>
//       </Modal>

//       {/* Upload Excel Modal */}
//       <Modal
//         show={showExcelModal}
//         onHide={() => {
//           setShowExcelModal(false);
//           setExcelData([]);
//           setExcelHeaders([]);
//           setSelectedFile(null);
//           if (fileInputRef.current) {
//             fileInputRef.current.value = "";
//           }
//         }}
//         size="lg"
//         centered
//         className="tbc-excel-modal"
//         dialogClassName="tbc-excel-dialog"
//       >
//         <Modal.Header closeButton className="tbc-excel-header">
//           <Modal.Title className="tbc-excel-title">
//             <i className="fas fa-file-excel tbc-excel-icon"></i>
//             Upload Tasks from Excel
//           </Modal.Title>
//         </Modal.Header>
//         <Modal.Body className="tbc-excel-body">
//           <Alert variant="info" className="tbc-excel-info">
//             <strong className="tbc-excel-info-title">How to use:</strong>
//             <ol className="tbc-excel-steps">
//               <li>Prepare your Excel file (.xlsx, .xls, or .csv)</li>
//               <li>First row should contain column headers</li>
//               <li>Each subsequent row represents one task</li>
//               <li>Upload the file and map columns to task fields</li>
//               <li>Click "Add Tasks" to import</li>
//             </ol>
//           </Alert>

//           <div className="tbc-upload-area">
//             <Form.Group className="tbc-upload-group">
//               <Form.Label className="tbc-upload-label">
//                 <i className="fas fa-cloud-upload-alt"></i> Select Excel File:
//               </Form.Label>
//               <Form.Control
//                 type="file"
//                 ref={fileInputRef}
//                 accept=".xlsx,.xls,.csv"
//                 onChange={handleFileUpload}
//                 className="tbc-file-input"
//                 disabled={uploading}
//               />
//               {uploading && (
//                 <div className="tbc-uploading-indicator">
//                   <Spinner animation="border" size="sm" />
//                   <span className="ms-2">Uploading and parsing file...</span>
//                 </div>
//               )}
//               {selectedFile && !uploading && excelData.length > 0 && (
//                 <div className="tbc-file-info">
//                   <i className="fas fa-check-circle text-success"></i>
//                   <span className="ms-2">Loaded: {selectedFile.name} ({excelData.length} rows)</span>
//                 </div>
//               )}
//             </Form.Group>
//           </div>

//           {excelHeaders.length > 0 && excelData.length > 0 && (
//             <>
//               <div className="tbc-column-mapping">
//                 <h6 className="tbc-mapping-title">Step 2: Map Columns to Task Fields</h6>
//                 <Alert variant="warning" className="tbc-mapping-warning">
//                   <small>
//                     <i className="fas fa-info-circle tbc-warning-icon"></i>
//                     <strong>Note:</strong> "Task Name" is required. Select "Skip Column" for columns you don't want to import.
//                   </small>
//                 </Alert>
//                 <Row className="tbc-mapping-row">
//                   <Col md={6} className="tbc-mapping-col">
//                     {renderColumnSelect("taskName", "Task Name", true)}
//                   </Col>
//                   <Col md={6} className="tbc-mapping-col">
//                     {renderColumnSelect("taskDescription", "Description", false)}
//                   </Col>
//                   <Col md={6} className="tbc-mapping-col">
//                     {renderColumnSelect("assignedTo", "Assignee", false)}
//                   </Col>
//                   <Col md={6} className="tbc-mapping-col">
//                     {renderColumnSelect("taskStartDate", "Start Date", false)}
//                   </Col>
//                   <Col md={6} className="tbc-mapping-col">
//                     {renderColumnSelect("taskEndDate", "End Date", false)}
//                   </Col>
//                   <Col md={6} className="tbc-mapping-col">
//                     {renderColumnSelect("taskPriority", "Priority", false)}
//                   </Col>
//                   <Col md={6} className="tbc-mapping-col">
//                     {renderColumnSelect("taskStatus", "Status", false)}
//                   </Col>
//                   <Col md={6} className="tbc-mapping-col">
//                     {renderColumnSelect("taskType", "Task Type", false)}
//                   </Col>
//                   <Col md={6} className="tbc-mapping-col">
//                     {renderColumnSelect("stageBelong", "Stage", false)}
//                   </Col>
//                 </Row>
//               </div>

//               <div className="tbc-preview-section">
//                 <h6 className="tbc-preview-title">Preview (First 5 rows):</h6>
//                 <div className="tbc-preview-table-container">
//                   <Table size="sm" bordered className="tbc-preview-table">
//                     <thead className="tbc-preview-header">
//                       <tr>
//                         <th>Task Name</th>
//                         <th>Description</th>
//                         <th>Assignee</th>
//                         <th>Start Date</th>
//                         <th>End Date</th>
//                         <th>Priority</th>
//                         <th>Status</th>
//                         <th>Task Type</th>
//                         <th>Stage</th>
//                       </tr>
//                     </thead>
//                     <tbody className="tbc-preview-body">
//                       {excelData.slice(0, 5).map((row, idx) => (
//                         <tr key={idx} className="tbc-preview-row">
//                           <td className="tbc-preview-cell">{selectedColumns.taskName !== null ? row[selectedColumns.taskName] || "-" : "-"}</td>
//                           <td className="tbc-preview-cell">{selectedColumns.taskDescription !== null ? row[selectedColumns.taskDescription] || "-" : "-"}</td>
//                           <td className="tbc-preview-cell">{selectedColumns.assignedTo !== null ? row[selectedColumns.assignedTo] || "-" : "-"}</td>
//                           <td className="tbc-preview-cell">{selectedColumns.taskStartDate !== null ? row[selectedColumns.taskStartDate] || "-" : "-"}</td>
//                           <td className="tbc-preview-cell">{selectedColumns.taskEndDate !== null ? row[selectedColumns.taskEndDate] || "-" : "-"}</td>
//                           <td className="tbc-preview-cell">{selectedColumns.taskPriority !== null ? row[selectedColumns.taskPriority] || "Medium" : "Medium"}</td>
//                           <td className="tbc-preview-cell">{selectedColumns.taskStatus !== null ? row[selectedColumns.taskStatus] || "Not Started" : "Not Started"}</td>
//                           <td className="tbc-preview-cell">{selectedColumns.taskType !== null ? row[selectedColumns.taskType] || "-" : "-"}</td>
//                           <td className="tbc-preview-cell">{selectedColumns.stageBelong !== null ? row[selectedColumns.stageBelong] || "-" : "-"}</td>
//                         </tr>
//                       ))}
//                     </tbody>
//                   </Table>
//                 </div>
//                 <small className="tbc-preview-info">
//                   Total data rows: {excelData.length} | File: {selectedFile?.name}
//                 </small>
//               </div>
//             </>
//           )}
//         </Modal.Body>
//         <Modal.Footer className="tbc-excel-footer">
//           <Button variant="secondary" className="tbc-excel-cancel-btn" onClick={() => {
//             setShowExcelModal(false);
//             setExcelData([]);
//             setExcelHeaders([]);
//             setSelectedFile(null);
//             if (fileInputRef.current) {
//               fileInputRef.current.value = "";
//             }
//           }}>
//             Cancel
//           </Button>
//           <Button
//             variant="success"
//             className="tbc-excel-add-btn"
//             onClick={addTasksFromExcel}
//             disabled={excelData.length === 0 || selectedColumns.taskName === null}
//           >
//             <i className="fas fa-plus tbc-btn-icon"></i>
//             Add {excelData.length} Tasks
//           </Button>
//         </Modal.Footer>
//       </Modal>
//     </>
//   );
// };

// export default TaskBatchCreator;
